import styled from "@emotion/styled";
import { ThemeKey } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { RequiredThemeKeys, themed } from "@smartsheet/sds-theme-internal";
import React, { forwardRef } from "react";
import { HTMLAttributes } from "react";
import { useTheme } from "../../../theme/utils/useTheme";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface MenuSeparatorProps extends HTMLAttributes<HTMLHRElement> {
    clientId?: string;
}

export interface StyledMenuSeparatorProps extends MenuSeparatorProps {
    themeMode: RequiredThemeKeys;
}

export const menuSeparatorTheme = themed({
    color: {
        "lodestar-light": color("color--background--neutral--default"),
        "ursa-light": color("color--border--neutral--weaker"),
    },
    height: {
        "lodestar-light": "2px",
        "ursa-light": "1px",
    },
    gap: {
        // 11 * 2 + 2 = 24px total
        "lodestar-light": "11px 16px",
        // 12 * 2 + 1 = 25px total and that's fine per UX.
        "ursa-light": "4px 8px",
    },
    borderRadius: {
        "lodestar-light": "0px",
        "ursa-light": "3px",
    },
});

const StyledMenuSeparator = styled("hr")<StyledMenuSeparatorProps>`
    display: block;
    min-height: ${({ themeMode }) => menuSeparatorTheme.height(themeMode)};
    background-color: ${({ themeMode }) => menuSeparatorTheme.color(themeMode)};
    margin: ${({ themeMode }) => menuSeparatorTheme.gap(themeMode)};
    padding: 0;
    border: none;
    border-radius: ${({ themeMode }) => menuSeparatorTheme.borderRadius(themeMode)};
`;

/**
 * https://www.w3.org/TR/wai-aria-1.2/#separator
 */
export const MenuSeparator = forwardRef<HTMLHRElement, MenuSeparatorProps>((props, ref) => {
    const { key } = useTheme();
    const themeMode = key as ThemeKey;

    return (
        <StyledMenuSeparator
            {...props}
            ref={ref}
            role="separator"
            themeMode={themeMode}
            data-client-id={props.clientId}
        />
    );
});

MenuSeparator.displayName = "MenuSeparator";
